import classNames from 'classnames';
import Image from 'next/image';
import { useTheme } from 'next-themes';
import { memo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useAnalyticsUtilities } from '@air/analytics';
import { ChevronLeftIcon, ChevronRightIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Modal } from '@air/primitive-modal';

import { intercomIDs } from '~/constants/intercomIDs';
import {
  UPSELL_MODAL_NEXT_BUTTON,
  UPSELL_MODAL_PREV_BUTTON,
  UPSELL_MODAL_SLIDE_BUTTON,
  UPSELL_MODAL_TITLE,
} from '~/constants/testIDs';
import { useCarousel } from '~/hooks/useCarousel';

export type UpsellModalProps = Record<string, never>;

type CarouselItem = {
  id: string;
  title: string;
  description: string;
  lightImage: string;
  darkImage: string;
};

const carouselItems: CarouselItem[] = [
  {
    id: 'video-intelligence',
    title: 'Video Intelligence',
    description:
      'Discover, understand, and navigate your footage effortlessly with AI-powered Video Intelligence. Get instant keywords, summaries, transcriptions, and chapter markers without scrubbing through hours of content.',
    lightImage: '/assets/images/upsell-modal-video-intelligence-light.jpg',
    darkImage: '/assets/images/upsell-modal-video-intelligence-dark.jpg',
  },
  {
    id: 'facial-recognition',
    title: 'Facial Recognition',
    description:
      'Automatically detect and identify people across your entire workspace with our AI-powered Facial Recognition. Tag once and find anyone in seconds, saving countless hours of manual work while enhancing content discoverability and rights management.',
    lightImage: '/assets/images/upsell-modal-facial-recognition-light.jpg',
    darkImage: '/assets/images/upsell-modal-facial-recognition-dark.jpg',
  },
  {
    id: 'libraries',
    title: 'Libraries',
    description:
      "Create secure, access-controlled spaces for your team's work. Libraries let you organize by project, team, or purpose while ensuring only authorized members can access sensitive content. Bring structure and peace of mind to your workflow.",
    lightImage: '/assets/images/upsell-modal-libraries-light.jpg',
    darkImage: '/assets/images/upsell-modal-libraries-dark.jpg',
  },
];

export const UpsellModal = memo(({ onClose }: AirModalProps<UpsellModalProps>) => {
  const { currentSlide, currentItem, goToSlide, nextSlide, prevSlide } = useCarousel(carouselItems);
  const { theme } = useTheme();
  const { track } = useAnalyticsUtilities();

  const currentImage = theme === 'dark' ? currentItem.darkImage : currentItem.lightImage;

  useHotkeys(
    'arrowright',
    () => nextSlide(),
    {
      enabled: true,
      preventDefault: true,
    },
    [nextSlide],
  );

  useHotkeys(
    'arrowleft',
    () => prevSlide(),
    {
      enabled: true,
      preventDefault: true,
    },
    [prevSlide],
  );

  // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
  const handleExploreUpgradeClick = () => {
    const eventName = `clicked_${currentItem.id}_upsell_button`;
    track(eventName);
    onClose();
  };

  return (
    <Modal
      className="h-[499px] w-[584px] overflow-hidden rounded-8 border ads:border-ads-gray-5 border-gray-5 p-0 ring-0 focus-visible:ring-0"
      isOpen
      onDismiss={onClose}
    >
      <div className="flex h-full flex-col">
        <div className="relative flex h-1/2 items-center justify-center overflow-hidden ads:border-ads-gray-5 border-gray-5 border-b bg-gradient-to-br from-blue-3 to-blue-4">
          <Image src={currentImage} alt={currentItem.title} fill className="object-cover" sizes="584px" priority />
        </div>

        <div className="flex h-1/2 flex-col justify-between pt-6">
          <div className="flex flex-col gap-3 px-8">
            <h2
              className="text-left font-semibold ads:text-primary text-20 text-gray-12 leading-[1.3] tracking-tight"
              data-testid={UPSELL_MODAL_TITLE}
            >
              {currentItem.title}
            </h2>

            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                // biome-ignore lint/style/noMagicNumbers: PLA-1724 — pre-existing debt, not introduced by the icon migration
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {carouselItems.map((item) => (
                  <div key={item.id} className="w-full shrink-0">
                    <p className="text-left align-middle font-normal ads:text-secondary text-14 text-gray-11 leading-[20px] tracking-tight">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between px-6 pb-4">
            <div className="flex items-center gap-1 pl-2">
              <button
                onClick={prevSlide}
                className="flex size-8 items-center justify-center rounded-4 transition-colors ads:hover:bg-hover-subtle hover:bg-gray-3"
                aria-label="Previous feature"
                data-testid={UPSELL_MODAL_PREV_BUTTON}
                tabIndex={-1}
                type="button"
              >
                <ChevronLeftIcon className="ads:text-icon-default text-gray-10" size="md" />
              </button>

              <button
                onClick={nextSlide}
                className="flex size-8 items-center justify-center rounded-4 transition-colors ads:hover:bg-hover-subtle hover:bg-gray-3"
                aria-label="Next feature"
                data-testid={UPSELL_MODAL_NEXT_BUTTON}
                tabIndex={-1}
                type="button"
              >
                <ChevronRightIcon className="ads:text-icon-default text-gray-10" size="md" />
              </button>

              <div className="ml-4 flex gap-2">
                {carouselItems.map((_, index) => (
                  <button
                    key={index}
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                    onClick={() => goToSlide(index)}
                    className={classNames(
                      'size-2 rounded-full transition-colors focus:outline-none',
                      index === currentSlide ? 'ads:bg-ads-gray-10 bg-gray-10' : 'ads:bg-ads-gray-5 bg-gray-5',
                    )}
                    data-testid={UPSELL_MODAL_SLIDE_BUTTON}
                    data-slide={index}
                    aria-label={`Go to slide ${index + 1}`}
                    type="button"
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button appearance="ghost" color="gray" onClick={onClose}>
                Dismiss
              </Button>

              <Button
                appearance="filled"
                color="blue"
                size="large"
                data-intercomid={intercomIDs.UPSELL_EXPLORE_UPGRADE_BUTTON}
                onClick={handleExploreUpgradeClick}
              >
                Explore upgrade
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
});

UpsellModal.displayName = 'UpsellModal';
